import React, { useState, useRef, useEffect } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { ScrollArea } from '../ui/scroll-area';
import { Separator } from '../ui/separator';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '../ui/sheet';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../ui/alert-dialog';
import {
  Bot,
  Send,
  Loader2,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  Undo2,
  MessageSquare,
  Sparkles,
  ArrowRight,
  Clock,
  Activity,
  Plus,
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';
import { cn } from '../../lib/utils';
import { useProject } from '../../contexts/ProjectContext';
import { Action } from '../../lib/auth';

const AIScheduleAssistant = ({ scheduleId, scheduleName, onChangesApplied }) => {
  const { can } = useProject();
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [messages, setMessages] = useState([]);
  const [conversationId, setConversationId] = useState(null);
  const [pendingChanges, setPendingChanges] = useState([]);
  const [showApprovalDialog, setShowApprovalDialog] = useState(false);
  const [approving, setApproving] = useState(false);
  const [snapshots, setSnapshots] = useState([]);
  const messagesEndRef = useRef(null);

  // Load snapshots when panel opens
  useEffect(() => {
    if (isOpen && scheduleId) {
      loadSnapshots();
    }
  }, [isOpen, scheduleId]);

  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const loadSnapshots = async () => {
    try {
      const response = await api.get(`/ai-assistant/snapshots/${scheduleId}`);
      setSnapshots(response.data);
    } catch (error) {
      console.error('Failed to load snapshots:', error);
    }
  };

  const handleSendQuery = async () => {
    if (!query.trim() || loading) return;

    const userMessage = { role: 'user', content: query, timestamp: new Date() };
    setMessages(prev => [...prev, userMessage]);
    setQuery('');
    setLoading(true);

    try {
      const response = await api.post('/ai-assistant/query', {
        schedule_id: scheduleId,
        query: query,
        conversation_id: conversationId
      });

      setConversationId(response.data.conversation_id);

      const assistantMessage = {
        role: 'assistant',
        content: response.data.answer,
        timestamp: new Date(),
        proposed_changes: response.data.proposed_changes || [],
        requires_approval: response.data.requires_approval,
        warning: response.data.warning
      };

      setMessages(prev => [...prev, assistantMessage]);

      if (response.data.proposed_changes?.length > 0) {
        setPendingChanges(response.data.proposed_changes);
      }
    } catch (error) {
      console.error('AI query failed:', error);
      const errorMessage = {
        role: 'assistant',
        content: error.response?.data?.detail || 'Failed to get AI response. Please try again.',
        timestamp: new Date(),
        isError: true
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async () => {
    if (!conversationId) return;
    setApproving(true);

    try {
      const response = await api.post('/ai-assistant/approve', {
        schedule_id: scheduleId,
        conversation_id: conversationId,
        action: 'approve'
      });

      toast.success('Changes Applied!', {
        description: response.data.message
      });

      // Add system message
      setMessages(prev => [...prev, {
        role: 'system',
        content: `✅ ${response.data.changes_applied} change(s) applied. Snapshot created for rollback.`,
        timestamp: new Date(),
        snapshotId: response.data.version_snapshot_id
      }]);

      setPendingChanges([]);
      setShowApprovalDialog(false);
      loadSnapshots();
      onChangesApplied?.();
    } catch (error) {
      toast.error('Failed to apply changes', {
        description: error.response?.data?.detail || 'An error occurred'
      });
    } finally {
      setApproving(false);
    }
  };

  const handleReject = async () => {
    if (!conversationId) return;

    try {
      await api.post('/ai-assistant/approve', {
        schedule_id: scheduleId,
        conversation_id: conversationId,
        action: 'reject'
      });

      toast.info('Changes Rejected', {
        description: 'No modifications were made to the schedule.'
      });

      setMessages(prev => [...prev, {
        role: 'system',
        content: '❌ Proposed changes were rejected. No modifications made.',
        timestamp: new Date()
      }]);

      setPendingChanges([]);
      setShowApprovalDialog(false);
    } catch (error) {
      toast.error('Failed to reject changes');
    }
  };

  const handleRollback = async (snapshotId) => {
    try {
      const response = await api.post(`/ai-assistant/rollback/${snapshotId}`);
      
      toast.success('Rollback Successful!', {
        description: response.data.message
      });

      setMessages(prev => [...prev, {
        role: 'system',
        content: '↩️ Schedule rolled back to previous version.',
        timestamp: new Date()
      }]);

      loadSnapshots();
      onChangesApplied?.();
    } catch (error) {
      toast.error('Rollback Failed', {
        description: error.response?.data?.detail || 'An error occurred'
      });
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendQuery();
    }
  };

  const renderMessage = (msg, index) => {
    if (msg.role === 'user') {
      return (
        <div key={index} className="flex justify-end mb-3">
          <div className="bg-slate-900 text-white rounded-lg px-4 py-2.5 max-w-[85%]">
            <p className="text-sm">{msg.content}</p>
          </div>
        </div>
      );
    }

    if (msg.role === 'system') {
      return (
        <div key={index} className="flex justify-center mb-3">
          <div className="bg-blue-50 border border-blue-200 rounded-lg px-4 py-2 text-xs text-blue-700">
            {msg.content}
          </div>
        </div>
      );
    }

    // Assistant message
    return (
      <div key={index} className="flex justify-start mb-3">
        <div className={cn(
          "rounded-lg px-4 py-2.5 max-w-[90%]",
          msg.isError ? "bg-red-50 border border-red-200" : "bg-slate-100"
        )}>
          <div className="flex items-center gap-2 mb-1.5">
            <Bot className="h-4 w-4 text-slate-500" />
            <span className="text-xs font-medium text-slate-500">AI Assistant</span>
            {msg.warning && (
              <Badge variant="outline" className="text-[10px] text-amber-600 border-amber-300">
                <AlertTriangle className="h-2.5 w-2.5 mr-1" />
                Limited Scope
              </Badge>
            )}
          </div>
          <p className="text-sm text-slate-700 whitespace-pre-wrap">{msg.content}</p>
          
          {/* Proposed Changes */}
          {msg.proposed_changes?.length > 0 && (
            <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-md">
              <div className="flex items-center gap-2 mb-2">
                <Sparkles className="h-4 w-4 text-amber-600" />
                <span className="text-xs font-semibold text-amber-700 uppercase">
                  Proposed Changes ({msg.proposed_changes.length})
                </span>
              </div>
              <div className="space-y-2">
                {msg.proposed_changes.map((change, i) => (
                  <div key={i} className="text-xs bg-white p-2 rounded border border-amber-100">
                    <div className="flex items-center gap-2 mb-1">
                      {change.change_type === 'activity_create' ? (
                        <Plus className="h-3 w-3 text-emerald-500" />
                      ) : (
                        <Activity className="h-3 w-3 text-amber-500" />
                      )}
                      <span className="font-medium text-slate-700">{change.entity_name}</span>
                      <Badge 
                        variant="outline" 
                        className={cn(
                          "text-[9px]",
                          change.change_type === 'activity_create' 
                            ? "bg-emerald-50 text-emerald-700 border-emerald-200" 
                            : ""
                        )}
                      >
                        {change.change_type === 'activity_create' ? 'NEW ACTIVITY' : change.change_type}
                      </Badge>
                    </div>
                    
                    {/* Activity Creation Preview */}
                    {change.change_type === 'activity_create' && change.activity_data ? (
                      <div className="mt-2 space-y-1 text-slate-600">
                        <div className="grid grid-cols-2 gap-2 p-2 bg-emerald-50 rounded">
                          <div>
                            <span className="text-[10px] text-slate-400 uppercase">Activity ID</span>
                            <p className="font-mono text-slate-700">{change.activity_data.activity_id || 'Auto-generated'}</p>
                          </div>
                          <div>
                            <span className="text-[10px] text-slate-400 uppercase">Type</span>
                            <p className="capitalize text-slate-700">{change.activity_data.activity_type || 'task'}</p>
                          </div>
                          <div>
                            <span className="text-[10px] text-slate-400 uppercase">Duration</span>
                            <p className="text-slate-700">{change.activity_data.original_duration || 1} day(s)</p>
                          </div>
                          <div>
                            <span className="text-[10px] text-slate-400 uppercase">WBS</span>
                            <p className="text-slate-700 truncate" title={change.activity_data.wbs_id}>
                              {change.activity_data.wbs_name || change.activity_data.wbs_id || 'Not specified'}
                            </p>
                          </div>
                          {change.activity_data.planned_start && (
                            <div>
                              <span className="text-[10px] text-slate-400 uppercase">Plan Start</span>
                              <p className="text-slate-700">{change.activity_data.planned_start}</p>
                            </div>
                          )}
                          {change.activity_data.planned_finish && (
                            <div>
                              <span className="text-[10px] text-slate-400 uppercase">Plan Finish</span>
                              <p className="text-slate-700">{change.activity_data.planned_finish}</p>
                            </div>
                          )}
                          {change.activity_data.predecessors?.length > 0 && (
                            <div className="col-span-2">
                              <span className="text-[10px] text-slate-400 uppercase">Predecessors</span>
                              <p className="text-slate-700">{change.activity_data.predecessors.length} relationship(s)</p>
                            </div>
                          )}
                        </div>
                        <p className="text-slate-500 mt-1 italic">{change.reason}</p>
                      </div>
                    ) : (
                      <>
                        <div className="flex items-center gap-2 text-slate-500">
                          <span>{change.field}: {String(change.current_value)}</span>
                          <ArrowRight className="h-3 w-3" />
                          <span className="text-amber-700 font-medium">{String(change.proposed_value)}</span>
                        </div>
                        <p className="text-slate-500 mt-1 italic">{change.reason}</p>
                      </>
                    )}
                  </div>
                ))}
              </div>
              
              {msg.requires_approval && (
                <div className="flex gap-2 mt-3">
                  <Button
                    size="sm"
                    className="bg-emerald-600 hover:bg-emerald-700 text-xs"
                    onClick={() => setShowApprovalDialog(true)}
                  >
                    <CheckCircle2 className="h-3.5 w-3.5 mr-1" />
                    Review & Approve
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="text-xs text-red-600 hover:text-red-700 hover:bg-red-50"
                    onClick={handleReject}
                  >
                    <XCircle className="h-3.5 w-3.5 mr-1" />
                    Reject
                  </Button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    );
  };

  const suggestedQueries = [
    "Which activities are on the critical path?",
    "Why does this activity have zero float?",
    "What happens if we delay the foundation?",
    "How can we reduce the project duration?"
  ];

  // Self-gate (defense-in-depth; the mount site also gates): the assistant
  // applies schedule changes (approve -> live structure mutation), so it is
  // plan-level. view/collaborate must not get an apply control. Placed after
  // all hooks to respect the rules of hooks.
  if (!can(Action.MANAGE_SCHEDULES)) return null;

  return (
    <>
      <Sheet open={isOpen} onOpenChange={setIsOpen}>
        <SheetTrigger asChild>
          <Button
            variant="outline"
            size="sm"
            className="gap-2 text-xs border-purple-200 text-purple-700 hover:bg-purple-50"
          >
            <Bot className="h-4 w-4" />
            AI Assistant
          </Button>
        </SheetTrigger>
        <SheetContent className="w-[500px] sm:max-w-[500px] p-0 flex flex-col">
          <SheetHeader className="px-4 py-3 border-b bg-gradient-to-r from-purple-50 to-blue-50">
            <SheetTitle className="flex items-center gap-2 text-base">
              <Bot className="h-5 w-5 text-purple-600" />
              AI Schedule Assistant
            </SheetTitle>
            <SheetDescription className="text-xs">
              Ask questions about <span className="font-medium">{scheduleName}</span> or request changes (approval required)
            </SheetDescription>
          </SheetHeader>

          {/* Rollback Snapshots */}
          {snapshots.length > 0 && (
            <div className="px-4 py-2 bg-slate-50 border-b">
              <div className="flex items-center justify-between">
                <span className="text-[10px] font-medium text-slate-500 uppercase">
                  Version Snapshots ({snapshots.filter(s => !s.rolled_back).length})
                </span>
                {snapshots.filter(s => !s.rolled_back).length > 0 && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-6 px-2 text-[10px] text-amber-600 hover:text-amber-700"
                    onClick={() => handleRollback(snapshots.find(s => !s.rolled_back)?.id)}
                  >
                    <Undo2 className="h-3 w-3 mr-1" />
                    Rollback Latest
                  </Button>
                )}
              </div>
            </div>
          )}

          {/* Messages */}
          <ScrollArea className="flex-1 px-4 py-3">
            {messages.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full text-center py-8">
                <Bot className="h-12 w-12 text-purple-200 mb-4" />
                <p className="text-sm text-slate-500 mb-4">
                  Ask me anything about your schedule
                </p>
                <div className="space-y-2 w-full">
                  <p className="text-[10px] text-slate-400 uppercase font-medium">Suggested Questions</p>
                  {suggestedQueries.map((q, i) => (
                    <button
                      key={i}
                      className="w-full text-left text-xs px-3 py-2 rounded-md bg-slate-50 hover:bg-slate-100 text-slate-600 transition-colors"
                      onClick={() => setQuery(q)}
                    >
                      <MessageSquare className="h-3 w-3 inline mr-2 text-purple-400" />
                      {q}
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              <>
                {messages.map((msg, i) => renderMessage(msg, i))}
                {loading && (
                  <div className="flex justify-start mb-3">
                    <div className="bg-slate-100 rounded-lg px-4 py-3">
                      <div className="flex items-center gap-2 text-slate-500">
                        <Loader2 className="h-4 w-4 animate-spin" />
                        <span className="text-sm">Analyzing schedule...</span>
                      </div>
                    </div>
                  </div>
                )}
                <div ref={messagesEndRef} />
              </>
            )}
          </ScrollArea>

          {/* Input */}
          <div className="px-4 py-3 border-t bg-white">
            <div className="flex gap-2">
              <Input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Ask about critical path, delays, what-if..."
                className="text-sm"
                disabled={loading}
              />
              <Button
                onClick={handleSendQuery}
                disabled={!query.trim() || loading}
                size="icon"
                className="bg-purple-600 hover:bg-purple-700"
              >
                {loading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Send className="h-4 w-4" />
                )}
              </Button>
            </div>
            <p className="text-[10px] text-slate-400 mt-2 text-center">
              AI is read-only by default. Changes require your approval.
            </p>
          </div>
        </SheetContent>
      </Sheet>

      {/* Approval Dialog */}
      <AlertDialog open={showApprovalDialog} onOpenChange={setShowApprovalDialog}>
        <AlertDialogContent className="max-w-lg">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-amber-500" />
              Approve AI Changes?
            </AlertDialogTitle>
            <AlertDialogDescription>
              Review the proposed changes before applying. A version snapshot will be created for rollback.
            </AlertDialogDescription>
          </AlertDialogHeader>

          <div className="my-4 p-3 bg-slate-50 rounded-lg border max-h-60 overflow-y-auto">
            {pendingChanges.map((change, i) => (
              <div key={i} className="text-sm mb-3 last:mb-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className="font-medium">{change.entity_name}</span>
                  <Badge variant="outline" className="text-[10px]">{change.change_type}</Badge>
                </div>
                <div className="text-xs text-slate-500 flex items-center gap-1">
                  {change.field}: {String(change.current_value)} 
                  <ArrowRight className="h-3 w-3" /> 
                  <span className="text-emerald-600 font-medium">{String(change.proposed_value)}</span>
                </div>
              </div>
            ))}
          </div>

          <div className="flex items-center gap-2 p-2 bg-blue-50 rounded-md text-xs text-blue-700">
            <Clock className="h-4 w-4" />
            A version snapshot will be created. You can rollback anytime.
          </div>

          <AlertDialogFooter>
            <AlertDialogCancel disabled={approving}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleApprove}
              disabled={approving}
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              {approving ? (
                <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Applying...</>
              ) : (
                <><CheckCircle2 className="h-4 w-4 mr-2" /> Approve & Apply</>
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default AIScheduleAssistant;
